// Product Gallery Custom Element with Flickity Integration
class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.mediaCount = 0;
    this.mainFlickity = null;
    this.thumbsFlickity = null;
    this.zoomState = {
      scale: 1,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
    };
    this._initialized = false;
  }

  init() {
    // H2 (review adversariale) : idempotence obligatoire. Sans ce garde-fou,
    // connectedCallback() + un `shopify:section:load` mal séquencé créeraient
    // une 2e instance Flickity sur le même `.product-gallery__main-slides`.
    if (this._initialized) return;

    this.mainContainer = this.querySelector('.product-gallery__main');
    this.thumbsList = this.querySelector('[data-thumbs-list]');
    this.thumbBtns = this.querySelectorAll('[data-thumb-btn]');
    this.dotBtns = this.querySelectorAll('[data-gallery-dot]');
    this.counterEl = this.querySelector('[data-gallery-counter]');
    this.prevBtn = this.querySelector('[data-gallery-prev]');
    this.nextBtn = this.querySelector('[data-gallery-next]');
    this.fullscreenBtn = this.querySelector('[data-fullscreen-btn]');
    this.scrollBtnPrev = this.querySelector('[data-thumb-scroll="prev"]');
    this.scrollBtnNext = this.querySelector('[data-thumb-scroll="next"]');
    // Lightbox <dialog> (coquille vide) : reconstruite et pilotée par le module
    // autonome assets/gallery-lightbox.js, chargé à la demande au 1er clic.
    this.lightboxDialog = this.querySelector('.gallery-lightbox');

    // Le nombre de médias se lit sur les slides (source unique), PAS sur les
    // vignettes : à 1 média (T16) les vignettes/dots/flèches ne sont PAS rendus,
    // mais la slide unique doit quand même s'initialiser (Flickity gère 1 cellule).
    this.mediaCount = this.querySelectorAll('.product-gallery__slide').length;

    if (this.mediaCount > 0) {
      this.setupFlickity();
      this.bindEvents();
    }

    this.bindVariantImageChange();
    this.bindSectionLifecycle();

    this._initialized = true;
  }

  // theme.Product (assets/theme.js) dispatche `variantImageChange` sur le conteneur
  // de section (`[data-section-id]`), SANS `bubbles: true`. Un listener sur `document`
  // ou sur `this` (descendant) ne le recevrait jamais : il faut l'attacher sur la cible
  // exacte du dispatch, à savoir l'ancêtre `[data-section-id]`.
  bindVariantImageChange() {
    const sectionEl = this.closest('[data-section-id]');
    if (!sectionEl) return;

    this.variantImageChangeSection = sectionEl;
    this.variantImageChangeHandler = (evt) => this.onVariantImageChange(evt);
    sectionEl.addEventListener('variantImageChange', this.variantImageChangeHandler);
  }

  onVariantImageChange(evt) {
    const variant = evt.detail && evt.detail.variant;
    const mediaId = variant && variant.featured_media && variant.featured_media.id;
    if (!mediaId) return;

    const thumb = this.querySelector(`[data-media-id="${mediaId}"]`);
    const index = thumb ? parseInt(thumb.dataset.thumbBtn, 10) : NaN;

    // NB: `window.DEBUG_MODE` n'existe pas — tracking-core.liquid déclare `const DEBUG_MODE`
    // en scope lexical de script (pas une propriété window). On reprend le garde-fou déjà
    // utilisé ailleurs dans le thème (tracking-ecommerce.liquid) pour que ce log de preuve
    // fonctionne réellement plutôt que d'être mort en silence.
    if (typeof DEBUG_MODE !== 'undefined' && DEBUG_MODE) {
      console.log('[gallery] variantImageChange', mediaId, '-> index', index);
    }

    if (Number.isNaN(index)) return;
    this.selectSlide(index);
  }

  // Éditeur de thème : `shopify:section:load` / `shopify:section:unload` sont
  // dispatchés sur `document` (bubbling), `event.target` étant le conteneur de
  // SECTION rechargé/déchargé (`[data-section-id]`). On scope le handler à
  // CETTE galerie (evt.target === sectionEl OU sectionEl y est contenu) pour
  // ne pas réinitialiser toutes les galeries de la page à chaque changement
  // de setting ailleurs.
  //
  // H2 (review adversariale) : ne JAMAIS appeler `init()` seul sur
  // `shopify:section:load` — si le custom element a déjà été recréé par le
  // DOM diff de l'éditeur, `connectedCallback()` a déjà appelé `init()` une
  // 1ère fois ; un `init()` supplémentaire sans `destroy()` créerait une 2e
  // Flickity sur le même `.product-gallery__main-slides`. `destroy()` remet
  // `_initialized` à `false`, donc l'`init()` qui suit reconstruit toujours
  // exactement UNE instance, que l'élément ait été recréé ou réutilisé en
  // place.
  bindSectionLifecycle() {
    const sectionEl = this.closest('[data-section-id]');
    if (!sectionEl) return;

    const matchesThisSection = (evt) =>
      evt.target === sectionEl || (typeof evt.target?.contains === 'function' && evt.target.contains(sectionEl));

    this.sectionLoadHandler = (evt) => {
      if (!matchesThisSection(evt)) return;
      this.destroy();
      this.init();
    };

    this.sectionUnloadHandler = (evt) => {
      if (!matchesThisSection(evt)) return;
      this.destroy();
    };

    document.addEventListener('shopify:section:load', this.sectionLoadHandler);
    document.addEventListener('shopify:section:unload', this.sectionUnloadHandler);
  }

  setupFlickity() {
    // Wait for Flickity to be available
    if (typeof Flickity === 'undefined') {
      setTimeout(() => this.setupFlickity(), 100);
      return;
    }

    this.setupMainFlickity();
    this.setupThumbsFlickity();
    this.syncFlickitySliders();
    // T20 — état initial : Flickity a déjà sélectionné la cellule 0 de façon
    // synchrone (is-selected posé avant le retour du constructeur) ; on aligne
    // le focus clavier dessus sans attendre un premier événement `change`.
    this.updateSlideFocusability();
  }

  setupMainFlickity() {
    if (!this.mainContainer) return;

    // Find the slides container generated by Liquid
    const slidesContainer = this.mainContainer.querySelector('.product-gallery__main-slides');
    if (!slidesContainer) return;

    // Initialize Flickity for main gallery on existing slides
    this.mainFlickity = new Flickity(slidesContainer, {
      // 'center' partout : en mobile la carte est centree dans la colonne de page
      // et laisse apparaitre le debut des slides voisines des DEUX cotes.
      cellAlign: 'center',
      contain: true,
      draggable: true,
      pageDots: false,
      prevNextButtons: false,
      wrapAround: true,
      adaptiveHeight: true,
      setGallerySize: false,
      resize: true,
    });

    // Clic/tap sur l'image -> plein écran, sur TOUS les viewports (en mobile le
    // bouton plein écran est masqué par la maquette, donc c'est le seul accès).
    // staticClick ne se déclenche que sur un vrai clic (pas un drag/swipe).
    this.mainFlickity.on('staticClick', (event) => {
      // Une vidéo, un modèle 3D ou un lien gardent leur comportement propre :
      // seule l'image ouvre le plein écran (la lightbox est images-only).
      if (event.target.closest('video, iframe, model-viewer, a, button')) return;

      // Déléguer au bouton plein écran plutôt qu'à openLightbox() direct, pour que
      // le listener de tracking délégué capte le clic (data-cta-name) et pousse
      // l'event dataLayer — sinon ces ouvertures ne seraient pas comptées.
      if (this.fullscreenBtn) this.fullscreenBtn.click();
      else this.openLightbox();
    });

    // Peek mobile (T14) : sous 768px, la CSS retrecit la cellule a 80%
    // (.grid--product-images--partial .product-gallery__slide). Flickity fige les
    // positions des cellules en px a l'init ; on force un recalcul apres le layout
    // pour que le decalage tienne compte de la largeur reduite (sinon la slide
    // suivante ne depasse pas). Le franchissement de 768px est deja couvert par
    // handleResize() (qui appelle resize()).
    if (window.innerWidth < 769) {
      requestAnimationFrame(() => {
        if (this.mainFlickity) this.mainFlickity.resize();
      });
    }
  }

  setupThumbsFlickity() {
    if (!this.thumbsList) return;

    // Mobile (< 768px) : la navigation passe par les dots (T13) et la bande de
    // vignettes est masquée en CSS. On n'initialise donc PAS de Flickity vignettes
    // (elle tournerait sur un élément display:none → mesures nulles inutiles).
    if (window.innerWidth < 769) return;

    // Desktop: colonne de vignettes VERTICALE à gauche (pas de Flickity, scroll natif)
    this.thumbsList.style.overflowX = 'hidden';
    this.thumbsList.style.overflowY = 'auto';
    this.thumbsList.style.height = '100%';

    // Add smooth scrolling behavior
    this.thumbsList.style.scrollBehavior = 'smooth';

    // Setup scroll detection for gradient animation
    this.setupScrollDetection();
  }

  setupScrollDetection() {
    if (!this.thumbsList || window.innerWidth < 769) return;

    const thumbsContainer = this.querySelector('.product-gallery__thumbs-container');
    if (!thumbsContainer) return;

    // Throttled scroll handler for performance — axe VERTICAL (colonne de
    // vignettes a gauche de l'image) : le fondu/bouton se lisent en haut et en bas.
    const handleScroll = this.throttle(() => {
      const scrollTop = this.thumbsList.scrollTop;
      const scrollHeight = this.thumbsList.scrollHeight;
      const clientHeight = this.thumbsList.clientHeight;

      // Position dans la colonne (petite tolerance) : masque le fondu + le bouton
      // du cote atteint. --scroll-start masque l'affordance du HAUT (au debut),
      // --scroll-end celle du BAS (a la fin).
      const isAtStart = scrollTop <= 5;
      const isAtEnd = scrollTop + clientHeight >= scrollHeight - 5;

      thumbsContainer.classList.toggle('product-gallery__thumbs-container--scroll-start', isAtStart);
      thumbsContainer.classList.toggle('product-gallery__thumbs-container--scroll-end', isAtEnd);
    }, 16); // ~60fps

    // Add scroll listener
    this.thumbsList.addEventListener('scroll', handleScroll);

    // Initial check
    handleScroll();

    // Store reference for cleanup
    this.scrollHandler = handleScroll;
  }

  syncFlickitySliders() {
    if (!this.mainFlickity) return;

    // Main slider change updates thumbnails
    this.mainFlickity.on('change', (index) => {
      this.currentIndex = index;
      // T17 — une seule video active a la fois : la galerie possede le carrousel
      // (theme.Product early-return), donc c'est ici qu'on met en pause la video
      // sortante quand la slide change.
      this.pauseInactiveVideos(index);
      this.updateActiveThumb();
      this.updateActiveDot();
      this.updateCounter();
      this.updateArrows();
      this.updateSlideFocusability();

      // Sync thumbnail slider
      if (this.thumbsFlickity) {
        this.thumbsFlickity.selectCell(index, false, true);
      } else {
        // Scroll to active thumb on desktop
        this.scrollToActiveThumb();
      }

      // Dispatch custom event
      this.dispatchEvent(
        new CustomEvent('slideChange', {
          detail: { index, mediaId: this.thumbBtns[index]?.dataset.mediaId },
        }),
      );
    });

    // Remove existing click handlers and add new ones
    this.thumbBtns.forEach((thumb, index) => {
      // Remove any existing listeners
      const newThumb = thumb.cloneNode(true);
      thumb.parentNode.replaceChild(newThumb, thumb);

      // Add new click handler
      newThumb.addEventListener('click', (e) => {
        e.preventDefault();
        this.mainFlickity.select(index);
      });
    });

    // Update thumbBtns reference after cloning
    this.thumbBtns = this.querySelectorAll('[data-thumb-btn]');
  }

  bindEvents() {
    // Arrow navigation
    this.prevBtn?.addEventListener('click', () => this.previousSlide());
    this.nextBtn?.addEventListener('click', () => this.nextSlide());

    // Dots de pagination (mobile) : chaque dot cible sa slide via l'API publique
    // goToSlide (comme les flèches/vignettes). Lié une seule fois dans init().
    this.dotBtns.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Plein écran : charge la lightbox <dialog> à la demande (import dynamique).
    this.fullscreenBtn?.addEventListener('click', () => this.openLightbox());

    // Scroll thumbnails (desktop) — bande horizontale bidirectionnelle
    this.scrollBtnPrev?.addEventListener('click', () => this.scrollThumbs('prev'));
    this.scrollBtnNext?.addEventListener('click', () => this.scrollThumbs('next'));

    // Keyboard navigation — restreint au focus DANS la galerie : capturer
    // aussi `document.activeElement === document.body` volait les flèches de
    // TOUTE la page (scroll cassé dès qu'aucun élément n'a le focus).
    // Quand la lightbox <dialog> est ouverte, elle possède ses PROPRES gestes
    // clavier (flèches + Escape, cf. gallery-lightbox.js) : on sort tôt pour ne
    // pas piloter la galerie inline en doublon. `hasAttribute('open')` couvre le
    // natif (show() pose l'attribut) ET le repli (setAttribute).
    this.keyboardHandler = (e) => {
      if (this.lightboxDialog && this.lightboxDialog.hasAttribute('open')) return;
      if (this.contains(document.activeElement)) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.previousSlide();
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.nextSlide();
        }
      }
    };
    document.addEventListener('keydown', this.keyboardHandler);

    // Handle window resize for responsive Flickity
    this._lastGalleryWidth = window.innerWidth;
    this.resizeHandler = this.debounce(() => {
      // Sur mobile, l'apparition/disparition de la barre d'adresse au scroll déclenche un resize
      // (hauteur seule). Les carrousels étant horizontaux, seule la LARGEUR impose un re-layout :
      // on ignore les resizes à largeur constante, sinon handleResize détruit/recrée la Flickity
      // des vignettes à chaque scroll → elles "poppent" (disparaissent puis réapparaissent).
      if (window.innerWidth === this._lastGalleryWidth) return;
      this._lastGalleryWidth = window.innerWidth;
      this.handleResize();
    }, 250);
    window.addEventListener('resize', this.resizeHandler);
  }

  goToSlide(index) {
    if (index < 0 || index >= this.mediaCount) return;

    if (this.mainFlickity) {
      this.mainFlickity.select(index);
    }
  }

  // T17 — pont play/pause en DOM direct (aucune dependance a theme.Product, dont
  // `stopVideos`/`videoObjects` sont prives a sa closure). Met en pause toute video
  // qui n'est PAS dans la slide active -> garantit « 0 video en lecture simultanee »
  // et pause la video sortante au changement de slide. `activeIndex = -1` pause tout
  // (utilise a l'ouverture de la lightbox images-only, pour couper un son residuel).
  pauseInactiveVideos(activeIndex) {
    const slides = this.querySelectorAll('.product-gallery__slide');
    slides.forEach((slide, i) => {
      if (i === activeIndex) return;
      slide.querySelectorAll('video').forEach((video) => {
        if (typeof video.pause === 'function' && video.paused === false) {
          video.pause();
        }
      });
      // Iframes YouTube : API postMessage (best-effort, no-op si enablejsapi absent).
      slide.querySelectorAll('iframe').forEach((frame) => {
        if (frame.contentWindow) {
          frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        }
      });
    });
  }

  previousSlide() {
    if (this.mainFlickity) {
      this.mainFlickity.previous();
    }
  }

  nextSlide() {
    if (this.mainFlickity) {
      this.mainFlickity.next();
    }
  }

  updateActiveThumb() {
    this.thumbBtns.forEach((btn, index) => {
      btn.classList.toggle('product-gallery__thumb--active', index === this.currentIndex);
    });
  }

  // Dots (mobile) : marque l'actif (visuel allongé + aria-current pour l'a11y).
  updateActiveDot() {
    this.dotBtns.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('product-gallery__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  // T20 — Flickity pose déjà `aria-hidden="true"` sur les cellules non
  // sélectionnées (Cell.prototype.unselect, cf. assets/vendor-scripts-v11.js)
  // mais ça ne retire PAS du tab order leurs descendants interactifs (trigger
  // vidéo, <video controls>, bouton de fermeture du model-viewer) : un Tab peut
  // encore y atterrir alors qu'ils sont masqués/hors-écran pour les AT -> piège
  // de focus. Cette build de Flickity ne clone AUCUN nœud pour `wrapAround`
  // (repositionnement des cellules existantes via transform, cf. `wrapShift`),
  // donc `.product-gallery__slide` énumère exactement les N slides réelles,
  // sans doublon à gérer.
  updateSlideFocusability() {
    const slides = this.querySelectorAll('.product-gallery__slide');
    slides.forEach((slide) => {
      const isSelected = slide.classList.contains('is-selected');
      const focusables = slide.querySelectorAll('a[href], button, video, model-viewer');
      focusables.forEach((el) => {
        if (isSelected) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  // Compteur « x / total » : on reconstruit depuis le template i18n interpolé
  // (data-counter-template, placeholder __CURRENT__) plutôt qu'une concaténation
  // codée en dur — le format « x / y » reste piloté par les locales.
  updateCounter() {
    if (!this.counterEl) return;
    const template = this.counterEl.dataset.counterTemplate;
    if (!template) return;
    this.counterEl.textContent = template.replace('__CURRENT__', this.currentIndex + 1);
  }

  updateArrows() {
    if (this.prevBtn) {
      this.prevBtn.style.opacity = this.mediaCount <= 1 ? '0.3' : '1';
      this.prevBtn.disabled = this.mediaCount <= 1;
    }
    if (this.nextBtn) {
      this.nextBtn.style.opacity = this.mediaCount <= 1 ? '0.3' : '1';
      this.nextBtn.disabled = this.mediaCount <= 1;
    }
  }

  scrollThumbs(direction = 'next') {
    if (this.thumbsFlickity) {
      if (direction === 'prev') {
        this.thumbsFlickity.previous();
      } else {
        this.thumbsFlickity.next();
      }
      return;
    }
    if (window.innerWidth < 769 || !this.thumbsList) return;

    // Defile d'un paquet de vignettes (max 3). scrollBy est borne automatiquement
    // aux extremites → pas de garde isAtStart/isAtEnd (le bouton est masque + non
    // cliquable au bord via .product-gallery__thumbs-container--scroll-start/end).
    const thumbHeight = 52 + 10; // hauteur vignette + gap (cf. .product-gallery__thumb)
    const clientHeight = this.thumbsList.clientHeight;
    const visibleThumbs = Math.max(1, Math.floor(clientHeight / thumbHeight));
    const scrollAmount = thumbHeight * Math.min(visibleThumbs, 3);

    this.thumbsList.scrollBy({
      top: direction === 'prev' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }

  // Scroll thumbnails (colonne verticale) to show the active thumb
  scrollToActiveThumb() {
    if (window.innerWidth < 769 || !this.thumbsList) return;

    const activeThumb = this.thumbBtns[this.currentIndex];
    if (!activeThumb) return;

    const thumbsList = this.thumbsList;

    // Get positions (axe vertical)
    const containerHeight = thumbsList.clientHeight;
    const thumbTop = activeThumb.offsetTop;
    const thumbHeight = activeThumb.offsetHeight;

    // Calculate if thumb is visible
    const scrollTop = thumbsList.scrollTop;
    const visibleTop = scrollTop;
    const visibleBottom = scrollTop + containerHeight;

    // Check if thumb is not fully visible
    if (thumbTop < visibleTop || thumbTop + thumbHeight > visibleBottom) {
      // Center the active thumb in the container
      const targetScroll = thumbTop - containerHeight / 2 + thumbHeight / 2;

      thumbsList.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: 'smooth',
      });
    }
  }

  // Plein écran : charge le module autonome de lightbox à la demande (import
  // dynamique au 1er clic), puis délègue l'ouverture. product-gallery.js reste un
  // script CLASSIQUE : un spécificateur relatif se résoudrait contre l'URL du
  // document et perdrait le cache-bust `?v=` de Shopify → on passe l'URL asset_url
  // via data-attribute (`this.dataset.lightboxSrc`).
  async openLightbox() {
    // Garde de ré-entrance (double-tap mobile pendant le chargement) : empêche deux
    // imports/ouvertures concurrents qui corrompraient le verrou de scroll → page gelée
    // (cf. review adversariale). La garde `isOpen` de GalleryLightbox.open() est le 2e filet.
    if (this._lightboxOpening) return;
    this._lightboxOpening = true;
    const src = this.dataset.lightboxSrc;
    if (!src) {
      this._lightboxOpening = false;
      return;
    }
    // Lightbox = images-only : on met en pause toute video inline pour eviter un
    // son qui continuerait derriere le <dialog> (T17).
    this.pauseInactiveVideos(-1);
    try {
      const module = await import(src);
      if (module && typeof module.openGalleryLightbox === 'function') {
        module.openGalleryLightbox(this, this.currentIndex);
      }
    } catch (err) {
      if (typeof DEBUG_MODE !== 'undefined' && DEBUG_MODE) {
        console.error('[gallery] chargement lightbox échoué', err);
      }
    } finally {
      this._lightboxOpening = false;
    }
  }

  handleResize() {
    const isMobile = window.innerWidth < 769;
    const wasFlickityActive = !!this.thumbsFlickity;
    const currentIndex = this.currentIndex;

    // Resize main Flickity. cellAlign reste 'center' quel que soit le viewport
    // (cf. setupMainFlickity) : plus de bascule à gérer ici.
    if (this.mainFlickity) {
      this.mainFlickity.resize();
    }

    // Always destroy existing thumbs Flickity on resize
    if (this.thumbsFlickity) {
      this.thumbsFlickity.destroy();
      this.thumbsFlickity = null;
      // Clean up Flickity inline styles
      this.cleanupFlickityStyles();
    }

    // Clean up scroll handler if switching from desktop to mobile
    if (this.scrollHandler && this.thumbsList && isMobile) {
      this.thumbsList.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }

    // Setup thumbs based on new screen size
    this.setupThumbsFlickity();

    // Force Flickity to recalculate dimensions and height
    if (this.mainFlickity) {
      this.mainFlickity.resize();
      this.mainFlickity.reloadCells();
    }

    // Re-sync if we have a new thumbs slider
    if (this.thumbsFlickity) {
      this.syncFlickitySliders();
      // Restore current index after resize
      this.thumbsFlickity.select(currentIndex, false, true);
    }

    // If we switched from mobile to desktop, ensure scroll detection is setup
    if (!isMobile && wasFlickityActive && !this.scrollHandler) {
      this.setupScrollDetection();
    }

    // Ensure active thumb is visible after resize
    setTimeout(() => {
      this.updateActiveThumb();
      if (!isMobile) {
        this.scrollToActiveThumb();
      }
    }, 100);
  }

  // Utility function for debouncing
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Utility function for throttling
  throttle(func, limit) {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  // Clean up Flickity inline styles
  cleanupFlickityStyles() {
    if (!this.thumbsList) return;

    // Remove Flickity classes and inline styles from container
    this.thumbsList.classList.remove('flickity-enabled');
    this.thumbsList.removeAttribute('style');
    this.thumbsList.removeAttribute('tabindex');

    // Clean up all thumb items
    this.thumbBtns.forEach((thumb) => {
      thumb.classList.remove('is-selected');
      thumb.removeAttribute('style');
      thumb.removeAttribute('aria-hidden');
      thumb.removeAttribute('tabindex');
    });

    // Remove any Flickity viewport wrapper if it exists
    const viewport = this.thumbsList.querySelector('.flickity-viewport');
    if (viewport) {
      const slider = viewport.querySelector('.flickity-slider');
      if (slider) {
        // Move all children back to the original container
        while (slider.firstChild) {
          this.thumbsList.appendChild(slider.firstChild);
        }
      }
      viewport.remove();
    }

    // Reset container styles for desktop mode (colonne verticale)
    if (window.innerWidth >= 769) {
      this.thumbsList.style.overflowX = 'hidden';
      this.thumbsList.style.overflowY = 'auto';
      this.thumbsList.style.height = '100%';
      this.thumbsList.style.scrollBehavior = 'smooth';
    }
  }

  // Public API methods
  selectSlide(index) {
    this.goToSlide(index);
  }

  getCurrentIndex() {
    return this.currentIndex;
  }

  getMediaCount() {
    return this.mediaCount;
  }

  // Cleanup method
  destroy() {
    // Remove event listeners
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.scrollHandler && this.thumbsList) {
      this.thumbsList.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.variantImageChangeSection && this.variantImageChangeHandler) {
      this.variantImageChangeSection.removeEventListener('variantImageChange', this.variantImageChangeHandler);
      this.variantImageChangeSection = null;
      this.variantImageChangeHandler = null;
    }
    if (this.sectionLoadHandler) {
      document.removeEventListener('shopify:section:load', this.sectionLoadHandler);
      this.sectionLoadHandler = null;
    }
    if (this.sectionUnloadHandler) {
      document.removeEventListener('shopify:section:unload', this.sectionUnloadHandler);
      this.sectionUnloadHandler = null;
    }

    // Destroy Flickity instances
    if (this.mainFlickity) {
      this.mainFlickity.destroy();
      this.mainFlickity = null;
    }
    if (this.thumbsFlickity) {
      this.thumbsFlickity.destroy();
      this.thumbsFlickity = null;
    }

    // H2 : rouvre la porte à un `init()` complet (ré-appelé par
    // connectedCallback() ou par le handler `shopify:section:load`).
    this._initialized = false;
  }

  // Lifecycle callbacks
  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }
}

// Register custom element
if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}

// Export for potential external use
window.ProductGallery = ProductGallery;
